import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { MarketSlug } from '../../../common/CountryMarketConfig';
import {
  CRM_DROPDOWN_MAX_OPTIONS,
  CRM_OTHER_DETAILS_MAX_LENGTH,
} from '../../../common/config';
import { intersection } from '../../../common/utils';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InputGroup } from '../../../components/InputGroup';
import { Textarea } from '../../../components/Textarea';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import {
  DropdownOption,
  DropdownOptionsWithNull,
  DropdownOptionsWithUndefined,
} from '../../../lib/Dropdown/types';
import { optionsInclude } from '../../../lib/Dropdown/utils';
import {
  isKeyInProductApplications,
  isKeyInProductApplicationsAtIndex,
} from '../../../lib/briefing';
import { noOp } from '../../../lib/utils';
import { StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import {
  formulationTypeOptions,
  productFormOptions,
  productMarketingClaimOptions,
  storageOptions,
  textureOptions,
} from './';
import {
  FormulationAttributesOption,
  TaxonomyKeyRepresentation,
} from './types';

type FormulationAttributesFieldConfig = {
  name: string;
  otherName: string;
  multiselect: boolean;
  available: DropdownOption[];
  initialValue: DropdownOptionsWithNull;
  otherInitialValue: string | null;
  onChange: ActionCreatorWithPayload<DropdownOptionsWithNull, string>;
  onOtherChange: ActionCreatorWithPayload<string | null, string>;
};

export const resetOnlyIfApplicationChanged = (
  option: DropdownOptionsWithNull,
  availableOptions: DropdownOption[],
  callback: () => void,
): void => {
  optionsInclude(option, availableOptions) || callback();
};

export const StepFormulationAttributes: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [
    {
      productApplications,
      productMarketingClaims,
      productMarketingClaimsOther,
      formulationType,
      texture,
      storage,
      productForm,
      formulationTypeOther,
      textureOther,
      storageOther,
      productFormOther,
    },
    {
      setProductMarketingClaims,
      setProductMarketingClaimsOther,
      setProductForm,
      setProductFormOther,
      setFormulationType,
      setTexture,
      setStorage,
      setFormulationTypeOther,
      setTextureOther,
      setStorageOther,
    },
  ] = useBriefingState();

  const [{ countryMarketConfig }] = useCountryMarketState();

  const filterByCountryMarketSpecificDeps = useCallback(
    (
      allOptions: FormulationAttributesOption[],
      country: string,
      market: MarketSlug,
      customFilter: (dep: TaxonomyKeyRepresentation) => boolean,
    ): FormulationAttributesOption[] => {
      const countryMarketIdentifier = `${country}|${market}`;
      const wildcardOptions = [
        countryMarketIdentifier,
        `${country}|*`,
        `*|${market}`,
        `*|*`,
      ];

      return allOptions.filter((option) => {
        const applicationKeysThatMatch = intersection(
          Object.keys(option.applications),
          wildcardOptions,
        );

        if (applicationKeysThatMatch.length === 0) return false;

        const applicationDeps = applicationKeysThatMatch
          .map((key) => option.applications[key])
          .flat();

        if (applicationDeps.length === 0) return true;
        if (applicationDeps.includes('*')) return true;

        return applicationDeps.some(customFilter);
      });
    },
    [],
  );

  const getDropDownOptionsFilteredByApplication = useCallback(
    (allOptions: FormulationAttributesOption[]): DropdownOption[] => {
      if (!countryMarketConfig) return [];

      const availableOptions: FormulationAttributesOption[] = [];

      // e.g. 'A_PC_001_003_006' || 'other'
      const deepestSelectedApplicationLevel = productApplications.slice(-1);
      // If "Other" has been selected...
      if (
        isKeyInProductApplications('other', deepestSelectedApplicationLevel)
      ) {
        // Include all if "Other" has been selected as the top-level application
        if (productApplications.length === 1) {
          availableOptions.push(...allOptions);
        } else {
          availableOptions.push(
            ...filterByCountryMarketSpecificDeps(
              allOptions,
              countryMarketConfig.eLabCountrySlug,
              countryMarketConfig.eLabMarketSlug,
              (key) =>
                isKeyInProductApplicationsAtIndex(
                  key,
                  productApplications,
                  productApplications.length - 2,
                ),
            ),
          );
        }
      } else {
        // But if "Other" has not been selected...
        availableOptions.push(
          ...filterByCountryMarketSpecificDeps(
            allOptions,
            countryMarketConfig.eLabCountrySlug,
            countryMarketConfig.eLabMarketSlug,
            (key) =>
              isKeyInProductApplications(key, deepestSelectedApplicationLevel),
          ),
        );
      }

      const options: DropdownOption[] = availableOptions.map((option) => ({
        name: t(option.name),
        value: option.value,
      }));

      options.push({
        name: t('placeholders:notApplicable'),
        value: 'notApplicable',
      });

      options.push({
        name: t('labels:other'),
        value: 'other',
      });

      return options;
    },
    [
      filterByCountryMarketSpecificDeps,
      productApplications,
      countryMarketConfig,
      t,
    ],
  );

  const availableProductMarketingClaims = useMemo(
    () => getDropDownOptionsFilteredByApplication(productMarketingClaimOptions),
    [getDropDownOptionsFilteredByApplication],
  );
  const availableFormulationTypes = useMemo(
    () => getDropDownOptionsFilteredByApplication(formulationTypeOptions),
    [getDropDownOptionsFilteredByApplication],
  );
  const availableTextures = useMemo(
    () => getDropDownOptionsFilteredByApplication(textureOptions),
    [getDropDownOptionsFilteredByApplication],
  );
  const availableStorage = useMemo(
    () => getDropDownOptionsFilteredByApplication(storageOptions),
    [getDropDownOptionsFilteredByApplication],
  );
  const availableProductForm = useMemo(
    () => getDropDownOptionsFilteredByApplication(productFormOptions),
    [getDropDownOptionsFilteredByApplication],
  );

  /**
   * * If selected texture and formulation types are no longer valid due to
   * * changes in application selection, unset these values in BriefingState
   */
  useEffect(() => {
    resetOnlyIfApplicationChanged(texture, availableTextures, () =>
      dispatch(setTexture(null)),
    );
    resetOnlyIfApplicationChanged(
      formulationType,
      availableFormulationTypes,
      () => dispatch(setFormulationType(null)),
    );
    resetOnlyIfApplicationChanged(storage, availableStorage, () =>
      dispatch(setStorage(null)),
    );
    resetOnlyIfApplicationChanged(productForm, availableProductForm, () =>
      dispatch(setProductForm(null)),
    );
    resetOnlyIfApplicationChanged(
      productMarketingClaims,
      availableProductMarketingClaims,
      () => dispatch(setProductMarketingClaims(null)),
    );
  }, [
    availableFormulationTypes,
    availableProductMarketingClaims,
    availableStorage,
    availableTextures,
    availableProductForm,
    dispatch,
    formulationType,
    productMarketingClaims,
    setFormulationType,
    setProductMarketingClaims,
    setStorage,
    setTexture,
    setProductForm,
    storage,
    productForm,
    texture,
  ]);

  const onDropDownChangeHandler = useCallback(
    (
      selection: DropdownOptionsWithUndefined,
      briefingStateAction: ActionCreatorWithPayload<
        DropdownOptionsWithNull,
        string
      >,
      briefingOtherStateAction: ActionCreatorWithPayload<string | null, string>,
    ) => {
      dispatch(briefingStateAction(selection || null));
      if (
        !selection ||
        (!Array.isArray(selection) && selection.value !== 'other') ||
        productMarketingClaims
          ?.map((marketingClaim) => marketingClaim.value)
          .includes('other')
      ) {
        dispatch(briefingOtherStateAction(null));
      }
    },
    [dispatch, productMarketingClaims],
  );

  const onOtherChangeHandler = useCallback(
    (
      event: ChangeEvent<HTMLTextAreaElement>,
      briefingStateReducer: ActionCreatorWithPayload<string | null, string>,
    ) => {
      event.persist();
      dispatch(briefingStateReducer(event.target.value));
    },
    [dispatch],
  );

  const [areOtherValuesValid, setAreOtherValuesValid] = useState<{
    [key: string]: boolean;
  }>({});
  const isStepValid = useMemo(
    () => !Object.values(areOtherValuesValid).some((value) => !value),
    [areOtherValuesValid],
  );
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const onOtherValidate = useCallback(
    (fieldConfig: FormulationAttributesFieldConfig, isValid: boolean) => {
      if (areOtherValuesValid[fieldConfig.otherName] === isValid) return;

      setAreOtherValuesValid({
        ...areOtherValuesValid,
        [fieldConfig.otherName]: isValid,
      });
    },
    [areOtherValuesValid],
  );

  const isOtherInputVisible = useCallback(
    (fieldConfig: FormulationAttributesFieldConfig) => {
      let isOtherVisible: boolean;
      if (Array.isArray(fieldConfig.initialValue)) {
        isOtherVisible = fieldConfig.initialValue.some(
          (option) => option && option.value === 'other',
        );
      } else {
        isOtherVisible = fieldConfig.initialValue?.value === 'other';
      }

      if (!isOtherVisible) {
        onOtherValidate(fieldConfig, true);
      }

      return isOtherVisible;
    },
    [onOtherValidate],
  );

  const getFormulationAttributesFieldsByMarket = useCallback(
    (marketSegment: MarketSlug): FormulationAttributesFieldConfig[] => {
      switch (marketSegment) {
        case MarketSlug.FoodNutrition:
          return [
            {
              name: 'productMarketingClaims',
              otherName: 'otherProductMarketingClaims',
              multiselect: true,
              available: availableProductMarketingClaims,
              initialValue: productMarketingClaims,
              otherInitialValue: productMarketingClaimsOther,
              onChange: setProductMarketingClaims,
              onOtherChange: setProductMarketingClaimsOther,
            },
            {
              name: 'storage',
              otherName: 'otherStorage',
              multiselect: false,
              available: availableStorage,
              initialValue: storage,
              otherInitialValue: storageOther,
              onChange: setStorage,
              onOtherChange: setStorageOther,
            },
          ];
        case MarketSlug.PersonalCare:
          return [
            {
              name: 'productMarketingClaims',
              otherName: 'otherProductMarketingClaims',
              multiselect: true,
              available: availableProductMarketingClaims,
              initialValue: productMarketingClaims,
              otherInitialValue: productMarketingClaimsOther,
              onChange: setProductMarketingClaims,
              onOtherChange: setProductMarketingClaimsOther,
            },
            {
              name: 'formulationType',
              otherName: 'otherFormulationType',
              multiselect: false,
              available: availableFormulationTypes,
              initialValue: formulationType,
              otherInitialValue: formulationTypeOther,
              onChange: setFormulationType,
              onOtherChange: setFormulationTypeOther,
            },
            {
              name: 'texture',
              otherName: 'otherTexture',
              multiselect: false,
              available: availableTextures,
              initialValue: texture,
              otherInitialValue: textureOther,
              onChange: setTexture,
              onOtherChange: setTextureOther,
            },
          ];
        case MarketSlug.AgriEnvironmental:
          // TODO: Add Attributes for `Agricultural & Environmental Solutions`
          return [];
        case MarketSlug.HomeCareIndustrialCleaning:
          // TODO: Add Attributes for `Home Care & Industrial Cleaning`
          return [
            {
              name: 'productMarketingClaims',
              otherName: 'otherProductMarketingClaims',
              multiselect: true,
              available: availableProductMarketingClaims,
              initialValue: productMarketingClaims,
              otherInitialValue: productMarketingClaimsOther,
              onChange: setProductMarketingClaims,
              onOtherChange: setProductMarketingClaimsOther,
            },
            {
              name: 'productForm',
              otherName: 'otherProductForm',
              multiselect: false,
              available: availableProductForm,
              initialValue: productForm,
              otherInitialValue: productFormOther,
              onChange: setProductForm,
              onOtherChange: setProductFormOther,
            },
          ];
      }
    },
    [
      formulationType,
      formulationTypeOther,
      productMarketingClaims,
      productMarketingClaimsOther,
      texture,
      textureOther,
      availableFormulationTypes,
      availableProductMarketingClaims,
      availableStorage,
      availableTextures,
      availableProductForm,
      storage,
      storageOther,
      productForm,
      productFormOther,
      setFormulationTypeOther,
      setProductMarketingClaimsOther,
      setStorageOther,
      setProductFormOther,
      setTextureOther,
      setFormulationType,
      setProductMarketingClaims,
      setStorage,
      setProductForm,
      setTexture,
    ],
  );

  if (!countryMarketConfig) return <></>;

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        text={t(`topics:formulation-attributes:heading:general`)}
        margin={0}
        optionalField={mandatoryFields['formulation-attributes']?.length === 0}
      />
      <div>
        <div className="grid md:grid-cols-3 gap-8">
          {getFormulationAttributesFieldsByMarket(
            countryMarketConfig.eLabMarketSlug,
          ).map((fieldConfig, index) => (
            <TemplateFlexGridContainer.BriefingStepSection key={index}>
              <div className="w-full">
                <InputGroup
                  label={t(`topics:formulation-attributes:${fieldConfig.name}`)}
                  errorMessage={
                    fieldConfig.available
                      ? ''
                      : t(
                          `topics:formulation-attributes:errors:${fieldConfig.name}`,
                        )
                  }
                >
                  <Dropdown
                    initialSelection={fieldConfig.initialValue || undefined}
                    options={fieldConfig.available}
                    error={!fieldConfig.available}
                    multiselect={fieldConfig.multiselect}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(option: DropdownOptionsWithUndefined) => {
                      onDropDownChangeHandler(
                        option,
                        fieldConfig.onChange,
                        fieldConfig.onOtherChange,
                      );
                    }}
                  />
                  {fieldConfig.multiselect && (
                    <p className="mt-1 text-gray-600 text-2xs font-regular">
                      {t('labels:multiSelectHint')}
                    </p>
                  )}
                </InputGroup>
              </div>
              <div className="w-full">
                {isOtherInputVisible(fieldConfig) && (
                  <InputGroup
                    label={t(
                      `topics:formulation-attributes:${fieldConfig.otherName}`,
                    )}
                  >
                    <div className="w-full __specification__ h-32">
                      <Textarea
                        placeholder={t('placeholders:textareaDefault')}
                        initialValue={
                          fieldConfig.otherInitialValue || undefined
                        }
                        onChange={(e) => {
                          onOtherChangeHandler(e, fieldConfig.onOtherChange);
                        }}
                        onValidate={(isValid) =>
                          onOtherValidate(fieldConfig, isValid)
                        }
                        maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                      />
                    </div>
                  </InputGroup>
                )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          ))}
        </div>
      </div>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
